const MMDDYYYY_PATTERN = /^\d{2}\/\d{2}\/\d{4}$/;

/**
 * Formats an ISO date string for display, e.g. "Jul 25" or, with `includeYear`, "Jul 25, 2026".
 * Always interprets/renders in UTC so the displayed day doesn't shift with the viewer's local
 * timezone. Returns the original string unchanged if it doesn't parse as a date.
 */
export function formatDisplayDate(isoDate: string, options?: { includeYear?: boolean }): string {
  const parsed = new Date(isoDate);
  if (Number.isNaN(parsed.getTime())) {
    return isoDate;
  }
  return parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
    timeZone: 'UTC',
    ...(options?.includeYear ? { year: 'numeric' as const } : {})
  });
}

/**
 * Formats an ISO date string as a full month name plus day, e.g. "September 9" — no year, no
 * leading zero on the day. Used where a headline reads more naturally with the month spelled out
 * (e.g. the AllOrdersTable expanded row's "Purchased September 9" / "Canceled September 3"
 * status line) than `formatDisplayDate`'s abbreviated "Sep 09". Returns the original string
 * unchanged if it doesn't parse as a date, same as `formatDisplayDate`.
 */
export function formatLongMonthDay(isoDate: string): string {
  const parsed = new Date(isoDate);
  if (Number.isNaN(parsed.getTime())) {
    return isoDate;
  }
  return parsed.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC'
  });
}

/** Parses a "MM/DD/YYYY" string into an ISO "YYYY-MM-DD" date, or undefined if malformed or not a real calendar date. */
export function parseMMDDYYYY(text: string): string | undefined {
  if (!MMDDYYYY_PATTERN.test(text)) {
    return undefined;
  }

  const [monthText, dayText, yearText] = text.split('/');
  const month = Number(monthText);
  const day = Number(dayText);
  const year = Number(yearText);

  if (!Number.isInteger(month) || !Number.isInteger(day) || !Number.isInteger(year)) {
    return undefined;
  }

  const parsed = new Date(Date.UTC(year, month - 1, day));
  if (
    parsed.getUTCFullYear() !== year ||
    parsed.getUTCMonth() !== month - 1 ||
    parsed.getUTCDate() !== day
  ) {
    return undefined;
  }

  return parsed.toISOString().slice(0, 10);
}

/** Formats an ISO "YYYY-MM-DD" date as "MM/DD/YYYY", or '' if malformed. */
export function formatMMDDYYYY(isoDate: string): string {
  const parts = isoDate.split('-');
  if (parts.length !== 3) {
    return '';
  }
  const [year, month, day] = parts;
  return `${month}/${day}/${year}`;
}

/**
 * Masks free-typed text into a "MM/DD/YYYY" shape as the user types, auto-inserting the next slash
 * once 2 or 5 digits have been entered. Skips the auto-slash when `previousLength` shows the user
 * is deleting rather than adding (backspacing over an auto-inserted slash shouldn't reinsert it).
 */
export function maskDateInput(raw: string, previousLength: number): string {
  const next = raw.replace(/[^0-9/]/g, '').slice(0, 10);
  const slashCount = (next.match(/\//g) ?? []).length;
  const shouldAutoSlash =
    (next.length === 2 && slashCount === 0 && previousLength !== 3) ||
    (next.length === 5 && slashCount === 1 && previousLength !== 6);
  return shouldAutoSlash ? `${next}/` : next;
}
